// src/pages/storePages/CheckoutPage.jsx
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../../context/CartContext";
import { useAuth } from "../../context/AuthContext";
import {
  storeCreateOrder,
  storeGetPaymentInfo,
} from "../../storeAPIs/StoreOrdersApis";
import "../../static/storecss/CheckoutPage.css";

const METHODS = [
  { id: "mpesa", label: "M-Pesa", icon: "fa-mobile-screen-button" },
  { id: "bank", label: "Bank", icon: "fa-building-columns" },
  { id: "paypal", label: "PayPal", icon: "fa-paypal" },
];

export default function CheckoutPage() {
  const { items, total, clear, count, ready } = useCart();
  const { user, isLoggedIn, isLoading } = useAuth();
  const navigate = useNavigate();

  const [payment, setPayment] = useState(null);
  const [method, setMethod] = useState("mpesa");
  const [deliveryNote, setDeliveryNote] = useState("");
  const [transactionId, setTransactionId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Gate: must be logged in
  useEffect(() => {
    if (isLoading) return;
    if (!isLoggedIn) navigate("/login?next=/checkout", { replace: true });
  }, [isLoggedIn, isLoading, navigate]);

  // Gate: cart must not be empty — but only after the cart has loaded
  useEffect(() => {
    if (!ready) return;
    if (count === 0) navigate("/store", { replace: true });
  }, [ready, count, navigate]);

  // Load payment destinations
  useEffect(() => {
    storeGetPaymentInfo()
      .then((res) => setPayment(res.data))
      .catch(() => setPayment({}));
  }, []);

  const idHint = useMemo(() => {
    switch (method) {
      case "mpesa":
        return "The code from your M-Pesa SMS (e.g. QDF7Y8X9Z1).";
      case "bank":
        return "Your bank transfer reference / slip number.";
      case "paypal":
        return "Your PayPal transaction ID.";
      default:
        return "";
    }
  }, [method]);

  const idPlaceholder = useMemo(() => {
    switch (method) {
      case "mpesa":
        return "e.g. QDF7Y8X9Z1";
      case "bank":
        return "e.g. TRF-849201";
      case "paypal":
        return "e.g. 5TY48210KP1234567";
      default:
        return "";
    }
  }, [method]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    const payload = {
      payment_method: method,
      transaction_id: transactionId,
      delivery_note: deliveryNote,
      items: items.map((i) => ({
        product: i.product_id,
        quantity: i.quantity,
      })),
    };

  try {
    await storeCreateOrder(payload);
    clear();
    navigate("/orders", { replace: true });
  } catch (err) {
      const data = err.response?.data;
      if (data && typeof data === "object") {
        setError(
          Object.entries(data)
            .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`)
            .join(" · ")
        );
      } else {
        setError(err.message || "Could not place order.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Wait for auth to resolve before rendering anything
  if (isLoading) {
    return (
      <div className="checkout-page">
        <div className="checkout-loading">
          <span className="checkout-spinner" />
          Checking session…
        </div>
      </div>
    );
  }

  if (!isLoggedIn) return null;

  // Wait for the cart to load — avoids bouncing to /store on refresh
  if (!ready) {
    return (
      <div className="checkout-page">
        <div className="checkout-loading">
          <span className="checkout-spinner" />
          Loading your cart…
        </div>
      </div>
    );
  }

  // Empty cart — show a friendly state instead of redirecting
  if (count === 0) {
    return (
      <div className="checkout-page">
        <div className="checkout-empty">
          <i className="fa-solid fa-cart-shopping" aria-hidden="true" />
          <h1>Your cart is empty</h1>
          <p>Add a product before checking out.</p>
          <Link to="/store" className="btn-primary">
            Browse store
          </Link>
        </div>
      </div>
    );
  }

  const userName =
    user?.first_name || user?.last_name
      ? `${user.first_name} ${user.last_name}`.trim()
      : user?.username || "Guest";

  return (
    <div className="checkout-page">
      {/* ── Header ── */}
      <header className="checkout-header">
        <div className="checkout-header-title">
          <h1>Checkout</h1>
          <p>Pay first, then paste the reference.</p>
        </div>
        <div className="checkout-steps" aria-hidden="true">
          <span className="checkout-step is-active">
            <span className="checkout-step-num">1</span> Pay
          </span>
          <span className="checkout-step-sep" />
          <span className="checkout-step">
            <span className="checkout-step-num">2</span> Confirm
          </span>
        </div>
      </header>

      <div className="checkout-grid">
        {/* ══ MAIN COLUMN ══ */}
        <div className="checkout-main">
          {/* Buyer + Payment */}
          <section className="checkout-card">
            <div className="checkout-buyer">
              <div className="checkout-buyer-avatar">
                {user?.profile_picture ? (
                  <img src={user.profile_picture} alt={userName} />
                ) : (
                  <i className="fa-solid fa-user" />
                )}
              </div>
              <div className="checkout-buyer-info">
                <strong>{userName}</strong>
                {(user?.email || user?.phone_number) && (
                  <span>
                    {[user?.email, user?.phone_number]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                )}
              </div>
            </div>

            <div className="checkout-block">
              <h2 className="checkout-block-title">
                <span className="checkout-block-num">1</span>
                Pay to
              </h2>

              <div className="checkout-method-grid" role="tablist">
                {METHODS.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    role="tab"
                    aria-selected={method === m.id}
                    className={`checkout-method-tab ${
                      method === m.id ? "is-active" : ""
                    }`}
                    onClick={() => setMethod(m.id)}
                  >
                    <i className={`fa-solid ${m.icon}`} />
                    <span>{m.label}</span>
                  </button>
                ))}
              </div>

              {method === "mpesa" && (
                <div className="checkout-pay-box">
                  <span className="checkout-pay-label">Till number</span>
                  <span className="checkout-pay-value">
                    {payment?.mpesa_till_number || "—"}
                  </span>
                  {payment?.mpesa_instructions && (
                    <p className="checkout-pay-instructions">
                      {payment.mpesa_instructions}
                    </p>
                  )}
                </div>
              )}

              {method === "bank" && (
                <div className="checkout-pay-box checkout-pay-box--bank">
                  <span className="checkout-pay-label">Bank details</span>
                  <pre className="checkout-pay-pre">
                    {payment?.bank_details || "—"}
                  </pre>
                  {payment?.bank_instructions && (
                    <p className="checkout-pay-instructions">
                      {payment.bank_instructions}
                    </p>
                  )}
                </div>
              )}

              {method === "paypal" && (
                <div className="checkout-pay-box checkout-pay-box--paypal">
                  <span className="checkout-pay-label">PayPal</span>
                  <span className="checkout-pay-value">
                    {payment?.paypal_details || "—"}
                  </span>
                  {payment?.paypal_instructions && (
                    <p className="checkout-pay-instructions">
                      {payment.paypal_instructions}
                    </p>
                  )}
                </div>
              )}
            </div>
          </section>

          {/* Confirm */}
          <form className="checkout-card" onSubmit={handleSubmit}>
            <h2 className="checkout-block-title">
              <span className="checkout-block-num">2</span>
              Confirm payment
            </h2>

            <div className="checkout-field">
              <label>Transaction / reference ID</label>
              <input
                type="text"
                placeholder={idPlaceholder}
                value={transactionId}
                onChange={(e) => setTransactionId(e.target.value)}
                required
                autoComplete="off"
              />
              <small className="checkout-hint">{idHint}</small>
            </div>

            <div className="checkout-field">
              <label>Delivery note (optional)</label>
              <textarea
                rows="2"
                value={deliveryNote}
                onChange={(e) => setDeliveryNote(e.target.value)}
                placeholder="Anything we should know?"
              />
            </div>

            {error && <div className="checkout-error">{error}</div>}

            <button
              type="submit"
              className="btn-primary checkout-submit"
              disabled={submitting}
            >
              {submitting ? "Placing order…" : "Confirm order"}
            </button>
          </form>
        </div>

        {/* ══ SIDEBAR SUMMARY ══ */}
        <aside className="checkout-summary">
          <header className="checkout-summary-head">
            <h2>Your order</h2>
            <span className="checkout-summary-count">
              {count} {count === 1 ? "item" : "items"}
            </span>
          </header>

          <ul className="checkout-summary-list">
            {items.map((it) => (
              <li key={it.id} className="checkout-summary-item">
                <div className="checkout-summary-thumb">
                  {it.primary_image ? (
                    <img src={it.primary_image} alt={it.name} />
                  ) : (
                    <i className="fa-solid fa-image" />
                  )}
                  <span className="checkout-summary-qty-badge">
                    {it.quantity}
                  </span>
                </div>

                <div className="checkout-summary-meta">
                  <span className="checkout-summary-name" title={it.name}>
                    {it.name}
                  </span>
                  <span className="checkout-summary-unit">
                    KES {Number(it.price).toLocaleString()} each
                  </span>
                </div>

                <span className="checkout-summary-line">
                  KES {(it.price * it.quantity).toLocaleString()}
                </span>
              </li>
            ))}
          </ul>

          <div className="checkout-summary-totals">
            <div className="checkout-summary-row">
              <span>Subtotal</span>
              <span>KES {Number(total).toLocaleString()}</span>
            </div>
            <div className="checkout-summary-row checkout-summary-row--muted">
              <span>Delivery</span>
              <span>Calculated at delivery</span>
            </div>
          </div>

          <div className="checkout-summary-grand">
            <span>Total</span>
            <strong>KES {Number(total).toLocaleString()}</strong>
          </div>

          <p className="checkout-summary-note">
            <i className="fa-solid fa-shield-halved" aria-hidden="true" />
            Your order is confirmed once the payment reference is verified.
          </p>
        </aside>
      </div>
    </div>
  );
}